'use client';

import * as React from 'react';
import { cx } from '../utils';
import { devWarn } from '../internal/devWarn';
import { useStableId } from '../hooks/useStableId';
import type { StyledProps, WithChildren } from '../types';

/* ── Collapsible (ADR-034) ─────────────────────────────────
   Low-level disclosure-content primitive. Owns animated open/
   closed transitions for a content panel paired with an
   external trigger (AccordionTrigger today; future <Accordion>
   composition tomorrow).

   Controlled-only: `open` is the sole state input. There is no
   internal state machine — adopters (or a higher composition
   such as Accordion) own the open/closed boolean and the
   single-open / multiple-open semantics.

   The trigger/content relationship is wired through ARIA:
   - Trigger emits `aria-expanded={open}` + `aria-controls={id}`
   - Collapsible carries the matching `id`. Adopters MUST supply
     a stable `id` and pass the same string to the trigger's
     `controls` prop so AT users hear the wiring. An auto-id is
     emitted when omitted so the rendered HTML is always
     well-formed, but the auto-id is opaque to the trigger; the
     pairing only works with adopter-supplied ids.

   Animation uses the grid-template-rows: 0fr ↔ 1fr trick to
   animate to height: auto without measuring children. The
   inner element owns `min-height: 0` + overflow clipping; the
   outer owns the grid-row interpolation. See kit.css
   `.j-collapsible` block and ADR-034 §3 for the contract.

   Focus management when closed: the panel renders with the
   `inert` attribute, which removes ALL descendants from the
   sequential focus order AND the accessibility tree. The
   prior approach using `aria-hidden` was insufficient —
   `aria-hidden` hides from AT but keyboard users could still
   Tab into a focusable descendant (link, button) inside a
   visibly-collapsed panel. `inert` is the spec-correct
   primitive for this case (HTML Living Standard §6.5.2;
   widely shipped in browsers since 2023). Codex review of
   ADR-034 implementation flagged the aria-hidden gap. */

export interface CollapsibleProps extends StyledProps, WithChildren {
  /** Controlled open state. There is no uncontrolled mode —
   *  the kit's only callers (AccordionTrigger today, future
   *  Accordion compositions tomorrow) already own state. */
  open: boolean;
  /** DOM id used by the paired trigger's `aria-controls`.
   *  Required for proper trigger/content pairing; an auto-id
   *  is emitted when omitted so the rendered HTML is always
   *  well-formed, but the auto-id is opaque to the trigger so
   *  pairing only works with adopter-supplied ids. The
   *  AccountDetails example in ADR-034 §1 shows the canonical
   *  shape. */
  id?: string;
  /** ARIA role for the disclosure panel. Defaults to `null`
   *  (no role) — most disclosures inside a sidebar/menu are
   *  not landmarks, and an un-named region clutters the
   *  landmark menu in screen readers. Pass `'region'` only
   *  when the panel is a top-level page section that deserves
   *  landmark navigation; if you do, also pass `ariaLabel` or
   *  `ariaLabelledBy` (a dev-time warn fires otherwise).
   *  Pass `'group'` for a non-landmark grouping (sub-sections
   *  of a settings page, FAQ items inside a longer doc). */
  panelRole?: 'region' | 'group' | null;
  /** Accessible name for the panel. Required when
   *  `panelRole === 'region'` per the WAI-ARIA APG landmark
   *  rule. Either `ariaLabel` or `ariaLabelledBy` MUST be
   *  provided in that case. */
  ariaLabel?: string;
  /** Id of an element whose text labels the panel. Use
   *  instead of `ariaLabel` when the paired trigger has
   *  visible text. */
  ariaLabelledBy?: string;
}

/* Inner subcomponent. Owns overflow clipping during animation.
   Adopters can pass the subcomponent explicitly for layout
   control (custom inner padding, scroll containers); when
   omitted, <Collapsible> auto-wraps children in a default
   `.j-collapsible__inner` so a bare
   `<Collapsible open={open}>content</Collapsible>` still
   animates correctly without ceremony. */
export interface CollapsibleInnerProps extends StyledProps, WithChildren {}

const CollapsibleInner = React.forwardRef<HTMLDivElement, CollapsibleInnerProps>(
  function CollapsibleInner({ className, children }, ref) {
    return (
      <div ref={ref} className={cx('j-collapsible__inner', className)}>
        {children}
      </div>
    );
  },
);

/* Detect whether children contain an explicit <Collapsible.Inner>.
   If yes, render them as-is (adopter owns the inner). If no, auto-
   wrap so the grid-row animation has a child to clip against. The
   detection is shallow — adopters who deeply nest Inner inside
   their own layout should still see the auto-wrap path because the
   outer wrapper still needs a single grid-row child. */
function hasExplicitInner(children: React.ReactNode): boolean {
  let found = false;
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === CollapsibleInner) {
      found = true;
    }
  });
  return found;
}

type CollapsibleComponent = React.ForwardRefExoticComponent<
  CollapsibleProps & React.RefAttributes<HTMLDivElement>
> & {
  Inner: typeof CollapsibleInner;
};

const CollapsibleBase = React.forwardRef<HTMLDivElement, CollapsibleProps>(function Collapsible(
  { open, id, panelRole = null, ariaLabel, ariaLabelledBy, className, children },
  ref,
) {
  const autoId = useStableId('jkit-collapsible');
  const resolvedId = id ?? autoId;
  const explicitInner = hasExplicitInner(children);

  /* Dev-warn when role="region" is requested without a name.
     An un-named region is a WAI-ARIA APG landmark anti-pattern:
     it clutters the screen reader's landmark menu with a generic
     "region" entry and forces users to read the contents to
     orient. Mirrors the Popover devWarn pattern (audit #23). */
  React.useEffect(() => {
    if (panelRole === 'region' && !ariaLabel && !ariaLabelledBy) {
      devWarn(
        '[JKit.Collapsible] panelRole="region" requires an accessible name. ' +
          'Pass `ariaLabel` (a string) or `ariaLabelledBy` (an id of a visible ' +
          'title element, typically the paired trigger). Unnamed regions clutter ' +
          'the landmark menu in screen readers. Use `panelRole="group"` or omit ' +
          'the role entirely (default) when the panel is not a landmark.',
      );
    }
  }, [panelRole, ariaLabel, ariaLabelledBy]);

  return (
    <div
      ref={ref}
      id={resolvedId}
      className={cx('j-collapsible', className)}
      data-state={open ? 'open' : 'closed'}
      role={panelRole ?? undefined}
      aria-label={panelRole !== null && !ariaLabelledBy ? ariaLabel : undefined}
      aria-labelledby={panelRole !== null ? ariaLabelledBy : undefined}
      /* `inert` on the COLLAPSED panel keeps focusable
         descendants out of the tab order AND the a11y tree.
         Beats the prior aria-hidden approach which left links
         and buttons inside a collapsed panel Tab-reachable (a
         keyboard-trap class of bug). When `open` is true, omit
         the attribute entirely — passing `inert={false}` would
         still emit `inert=""` (truthy) on React 18; passing
         `undefined` strips it cleanly. */
      {...(!open ? { inert: '' as unknown as boolean } : {})}
    >
      {explicitInner ? children : <CollapsibleInner>{children}</CollapsibleInner>}
    </div>
  );
}) as CollapsibleComponent;

CollapsibleBase.Inner = CollapsibleInner;

export const Collapsible = CollapsibleBase;
export { CollapsibleInner };
